import React, { createContext, PropsWithChildren, useEffect, useState } from "react";
import Animated, { useSharedValue, withTiming, useAnimatedStyle, Easing, runOnJS } from "react-native-reanimated";
import { ViewStyle } from "react-native";

type Rect = { width?: number; height?: number; x?: number; y?: number };

type Ctx = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setLocation: React.Dispatch<React.SetStateAction<Rect>>;
  setToolTipContent: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  setToolTipMessage: React.Dispatch<React.SetStateAction<string | null>>;
  show: (opts: { content?: React.ReactNode; message?: string; location?: Partial<Rect> }) => void;
  hide: () => void;
};

export const ToolTipContext = createContext<Ctx | undefined>(undefined);

export function ToolTipProvider({ children }: PropsWithChildren) {
  const [visible, setVisible] = useState(false);
  const [content, setToolTipContent] = useState<React.ReactNode>(null);
  const [message, setToolTipMessage] = useState<string | null>(null);

  const [location, setLocation] = useState<Rect>({
    width: 200,
    height: 200,
    x: 10,
    y: 10,
  });

  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.98);

  const animateIn = () => {
    opacity.value = withTiming(1, { duration: 200, easing: Easing.out(Easing.cubic) });
    scale.value = withTiming(1, { duration: 200, easing: Easing.out(Easing.cubic) });
  };
  const animateOut = (after?: () => void) => {
    opacity.value = withTiming(0, { duration: 200, easing: Easing.in(Easing.cubic) }, (finished) => {
      if (finished && after) runOnJS(after)();
    });
    scale.value = withTiming(0.98, { duration: 200, easing: Easing.in(Easing.cubic) });
  };

  useEffect(() => {
    if (visible) animateIn();
    else
      animateOut(() => {
        setToolTipContent(null);
        setToolTipMessage(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const aStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  const show: Ctx["show"] = ({ content: c, message: m, location: loc }) => {
    if (loc) {
      setLocation((prev) => ({
        width: loc.width ?? prev.width,
        height: loc.height ?? prev.height,
        x: loc.x ?? prev.x,
        y: loc.y ?? prev.y,
      }));
    }
    if (c !== undefined) setToolTipContent(c);
    if (m !== undefined) setToolTipMessage(m);
    setVisible(true);
  };

  const hide = () => setVisible(false);

  const rendered = content ?? (message ? <Animated.Text>{message}</Animated.Text> : null);

  return (
    <ToolTipContext.Provider
      value={{
        visible,
        setVisible,
        setLocation,
        setToolTipContent,
        setToolTipMessage,
        show,
        hide,
      }}
    >
      {children}

      {/* Tooltip layer */}
      <Animated.View
        pointerEvents={visible ? "auto" : "none"}
        style={[
          {
            position: "absolute",
            left: location.x,
            top: location.y,
            width: location.width,
            height: location.height,
            zIndex: 9999,
            elevation: 9999,
          } as ViewStyle,
          aStyle,
        ]}
      >
        {rendered}
      </Animated.View>
    </ToolTipContext.Provider>
  );
}
